import React, { createRef, useEffect, useState, useRef } from 'react';
import { Container, Button, ButtonGroup, Col, Row } from 'react-bootstrap';
import SweetAlert from 'react-bootstrap-sweetalert';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import classNames from 'classnames';
import { PaymentMethod } from 'stock-vault-www-site/src/constants/PaymentMethod';
import DealerSectionsNav from '../DealerSectionsNav';
import validationSchema from './forms/DealerForm.schema';
import DealerForm from './forms/DealerForm';
import {
  clearDealerDetails,
  clearDealerFeesValidationErrors,
  deleteDealer,
  editDealer,
  getDealer,
} from '../DealersActions';
import { DealerRoleName } from '../../constants/DealerRole';
import { DealerPositionName } from '../../constants/DealerPosition';
import DealerNotesForm from './forms/DealerNotesForm';
import PageBreadcrumb from '../../common/components/layout/PageBreadcrumb';
import { DealerStatus, DealerStatusActionName } from '../../constants/DealerStatus';
import { useQueryString } from '../../common/helpers/queryString';
import { pencesToPounds } from '../../common/helpers/pencesToPounds';
import { DealerUserStatusName } from '../../constants/UserStatus';
import ComponentCard from '../../common/components/cards/ComponentCard';
import { showAlert as showValidationAlert } from '../../common/components/alert/AlertActions';
import AlertVariant from '../../common/components/alert/AlertVariant';

function EditDealerPage() {
  const query = useQueryString();
  const { id } = useParams();
  const dispatch = useDispatch();
  const dealerNotesRef = createRef();
  const formikSetFieldValueRef = useRef(null);

  useEffect(() => {
    dispatch(getDealer(id));
  }, [dispatch, id]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(
    () => () => {
      dispatch(clearDealerFeesValidationErrors());
      dispatch(clearDealerDetails());
    },
    [dispatch],
  );

  const data = useSelector((state) => state.dealers.get('dealerDetails'));
  const [statusValue, setStatusValue] = useState();
  const [showAlert, setShowAlert] = useState(false);
  const [newStatusValue, setNewStatusValue] = useState(false);
  const [showNotes, setShowNotes] = useState({ show: false });
  const dealerNotes = useSelector((state) => state.dealers.get('dealerNotes'));
  const itemsNotes = dealerNotes?.items || null;

  const [searchParamsNotes, setSearchParamsNotes] = useState({
    offset: query.get('page') ? +query.get('page') - 1 : 0,
  });

  const changeOffsetNotes = (value) => {
    setSearchParamsNotes((prevState) => ({ ...prevState, offset: value }));
  };

  const mapAdditionalEmails = (additionalEmails) =>
    additionalEmails.map((item) => ({
      email: item,
    }));

  const mapPaymentMethod = () => {
    const paymentMethod = get(data, 'organization.paymentMethod', null);
    return {
      value: paymentMethod,
      label: paymentMethod === PaymentMethod.CREDIT_CARD ? 'Credit Card' : 'Credit Facility',
    };
  };

  const mainProfile = {
    profileId: get(data, 'mainProfile.id', '') || '',
    profileSerial: get(data, 'mainProfile.serial', '') || '',
    profileVersion: get(data, 'mainProfile.version', null),
    status: get(data, 'mainProfile.status', '') || '',
    firstName: get(data, 'mainProfile.firstName', '') || '',
    lastName: get(data, 'mainProfile.lastName', '') || '',
    email: get(data, 'mainProfile.email', '') || '',
    landlinePhoneNumber: get(data, 'mainProfile.landlinePhoneNumber', '') || '',
    phoneNumber: get(data, 'mainProfile.phoneNumber', '') || '',
    mailchimpId: get(data, 'mainProfile.mailchimpId', '') || '',
    position: {
      label: DealerPositionName[get(data, 'mainProfile.position', '')] || '',
      value: get(data, 'mainProfile.position', '') || '',
    },
    isBuyer: get(data, 'mainProfile.isBuyer', '') || false,
    isSeller: get(data, 'mainProfile.isSeller', '') || false,
    isOwner: get(data, 'mainProfile.isOwner', '') || '',
    acceptNewsletter: get(data, 'mainProfile.acceptNewsletter', '') || '',
    acceptTos: get(data, 'mainProfile.acceptTos', '') || '',
    promoCode: get(data, 'mainProfile.promoCode', '') || '',
  };
  const organization = {
    additionalEmails: mapAdditionalEmails(
      get(data, 'organization.additionalEmails', []) || [],
    ),
    accountManager: (() => {
      const amTop = get(data, 'adminManager', null);
      const amOrg = get(data, 'organization.accountManager', null);
      const am = amTop || amOrg;
      if (am && (am.id || am.memberId)) {
        const id = am.id || am.memberId;
        const label = am.fullName || [am.firstName, am.lastName].filter(Boolean).join(' ');
        return { value: id, label };
      }
      return '';
    })(),
    additionalSalesEmails: mapAdditionalEmails(
      get(data, 'organization.additionalSalesEmails', []) || [],
    ),
    organizationId: get(data, 'organization.id', '') || '',
    organizationVersion: get(data, 'organization.version', null),
    organizationSerial: get(data, 'organization.serial', '') || '',
    organizationFeesVersion: get(data, 'organization.organizationFees.version', null),
    organizationFeesSerial: get(data, 'organization.organizationFees.serial', '') || '',
    organizationGroup: get(data, 'organization.group', '') || '',
    officialCompanyName: get(data, 'organization.officialCompanyName', '') || '',
    tradeName: get(data, 'organization.tradeName', '') || '',
    buyerNextGearId: get(data, 'organization.buyerNextGearId', '') || '',
    sellerNextGearId: get(data, 'organization.sellerNextGearId', '') || '',
    vatNumber: get(data, 'organization.vatNumber', '') || '',
    companyRegistrationNumber: get(data, 'organization.companyRegistrationNumber', '') || null,
    companyRegistrationDate: get(data, 'mainProfile.registrationDate', '') || '',
    role: {
      label: DealerRoleName[get(data, 'organization.role', '')] || '',
      value: get(data, 'organization.role', '') || '',
    },
    website: get(data, 'organization.website', '') || '',
    paymentMethod: mapPaymentMethod(),
    monthlySubscriptionInPence: pencesToPounds(
      get(data, 'organization.organizationFees.monthlySubscriptionInPence', 0) || 0,
    ),
    monthlySubscriptionFeeExemption: {
      label: get(data, 'organization.organizationFees.monthlySubscriptionFeeExemption', '')
        ? 'Yes'
        : 'No',
      value:
        get(data, 'organization.organizationFees.monthlySubscriptionFeeExemption', '') || '',
    },
    monthlySubscriptionFeeExemptionTo:
      get(data, 'mainProfile.promoCode')?.trim()?.length > 0 &&
      get(data, 'mainProfile.status') === 'PENDING'
        ? ''
        : get(data, 'organization.organizationFees.monthlySubscriptionFeeExemptionTo', '') ||
          '',
    launchInPence:
      pencesToPounds(get(data, 'organization.organizationFees.launchInPence', 0)) || 0,
    launchFeeExemption:
      {
        label: get(data, 'organization.organizationFees.launchFeeExemption', '')
          ? 'Yes'
          : 'No',
        value: get(data, 'organization.organizationFees.launchFeeExemption', ''),
      } || '',
    launchFeeExemptionTo:
      get(data, 'organization.organizationFees.launchFeeExemptionTo', '') || '',
    saleForLessThan1000InPence:
      pencesToPounds(
        get(data, 'organization.organizationFees.saleForLessThan1000InPence', 0),
      ) || 0,
    saleForLessThan1000FeeExemption: {
      label: get(data, 'organization.organizationFees.saleForLessThan1000FeeExemption', '')
        ? 'Yes'
        : 'No',
      value:
        get(data, 'organization.organizationFees.saleForLessThan1000FeeExemption', '') || '',
    },
    saleForLessThan1000FeeExemptionTo:
      get(data, 'organization.organizationFees.saleForLessThan1000FeeExemptionTo', '') || '',
    saleBetween1001And5000InPence:
      pencesToPounds(
        get(data, 'organization.organizationFees.saleBetween1001And5000InPence', 0),
      ) || 0,
    saleBetween1001And5000FeeExemption: {
      label: get(data, 'organization.organizationFees.saleBetween1001And5000FeeExemption', '')
        ? 'Yes'
        : 'No',
      value:
        get(data, 'organization.organizationFees.saleBetween1001And5000FeeExemption', '') ||
        '',
    },
    saleBetween1001And5000FeeExemptionTo:
      get(data, 'organization.organizationFees.saleBetween1001And5000FeeExemptionTo', '') ||
      '',
    saleBetween5001And10000InPence:
      pencesToPounds(
        get(data, 'organization.organizationFees.saleBetween5001And10000InPence', 0),
      ) || 0,
    saleBetween5001And10000FeeExemption: {
      label: get(data, 'organization.organizationFees.saleBetween5001And10000FeeExemption', '')
        ? 'Yes'
        : 'No',
      value:
        get(data, 'organization.organizationFees.saleForLessThan1000FeeExemption', '') || '',
    },
    saleBetween5001And10000FeeExemptionTo:
      get(data, 'organization.organizationFees.saleBetween5001And10000FeeExemptionTo', '') ||
      '',
    saleBetween10001And15000InPence:
      pencesToPounds(
        get(data, 'organization.organizationFees.saleBetween10001And15000InPence', 0),
      ) || 0,
    saleBetween10001And15000FeeExemption: {
      label: get(
        data,
        'organization.organizationFees.saleBetween10001And15000FeeExemption',
        '',
      )
        ? 'Yes'
        : 'No',
      value:
        get(data, 'organization.organizationFees.saleForLessThan1000FeeExemption', '') || '',
    },
    saleBetween10001And15000FeeExemptionTo:
      get(data, 'organization.organizationFees.saleBetween10001And15000FeeExemptionTo', '') ||
      '',
    saleForMoreThan15000InPence:
      pencesToPounds(
        get(data, 'organization.organizationFees.saleForMoreThan15000InPence', 0),
      ) || 0,
    saleForMoreThan15000FeeExemption: {
      label: get(data, 'organization.organizationFees.saleForMoreThan15000FeeExemption', '')
        ? 'Yes'
        : 'No',
      value:
        get(data, 'organization.organizationFees.saleForLessThan1000FeeExemption', '') || '',
    },
    saleForMoreThan15000FeeExemptionTo:
      get(data, 'organization.organizationFees.saleForMoreThan15000FeeExemptionTo', '') || '',
    purchaseForLessThan1000InPence:
      pencesToPounds(
        get(data, 'organization.organizationFees.purchaseForLessThan1000InPence', 0),
      ) || 0,
    purchaseForLessThan1000FeeExemption: {
      label: get(data, 'organization.organizationFees.purchaseForLessThan1000FeeExemption', '')
        ? 'Yes'
        : 'No',
      value:
        get(data, 'organization.organizationFees.saleForLessThan1000FeeExemption', '') || '',
    },
    purchaseForLessThan1000FeeExemptionTo:
      get(data, 'organization.organizationFees.purchaseForLessThan1000FeeExemptionTo', '') ||
      '',
    purchaseBetween1001And5000InPence:
      pencesToPounds(
        get(data, 'organization.organizationFees.purchaseBetween1001And5000InPence', 0),
      ) || 0,
    purchaseBetween1001And5000FeeExemption: {
      label: get(
        data,
        'organization.organizationFees.purchaseBetween1001And5000FeeExemption',
        '',
      )
        ? 'Yes'
        : 'No',
      value:
        get(data, 'organization.organizationFees.saleForLessThan1000FeeExemption', '') || '',
    },
    purchaseBetween1001And5000FeeExemptionTo:
      get(
        data,
        'organization.organizationFees.purchaseBetween1001And2500FeeExemptionTo',
        '',
      ) || '',
    purchaseBetween5001And10000InPence:
      pencesToPounds(
        get(data, 'organization.organizationFees.purchaseBetween5001And10000InPence', 0),
      ) || 0,
    purchaseBetween5001And10000FeeExemption: {
      label: get(
        data,
        'organization.organizationFees.purchaseBetween5001And10000FeeExemption',
        '',
      )
        ? 'Yes'
        : 'No',
      value:
        get(data, 'organization.organizationFees.saleForLessThan1000FeeExemption', '') || '',
    },
    purchaseBetween5001And10000FeeExemptionTo:
      get(
        data,
        'organization.organizationFees.purchaseBetween5001And10000FeeExemptionTo',
        '',
      ) || '',
    purchaseBetween10001And15000InPence:
      pencesToPounds(
        get(data, 'organization.organizationFees.purchaseBetween10001And15000InPence', 0),
      ) || 0,
    purchaseBetween10001And15000FeeExemption: {
      label: get(
        data,
        'organization.organizationFees.purchaseBetween10001And15000FeeExemption',
        '',
      )
        ? 'Yes'
        : 'No',
      value:
        get(data, 'organization.organizationFees.saleForLessThan1000FeeExemption', '') || '',
    },
    purchaseBetween10001And15000FeeExemptionTo:
      get(
        data,
        'organization.organizationFees.purchaseBetween10001And15000FeeExemptionTo',
        '',
      ) || '',
    purchaseForMoreThan15000InPence:
      pencesToPounds(
        get(data, 'organization.organizationFees.purchaseForMoreThan15000InPence', 0),
      ) || 0,
    purchaseForMoreThan15000FeeExemption: {
      label: get(
        data,
        'organization.organizationFees.purchaseForMoreThan15000FeeExemption',
        '',
      )
        ? 'Yes'
        : 'No',
      value:
        get(data, 'organization.organizationFees.saleForLessThan1000FeeExemption', '') || '',
    },
    purchaseForMoreThan15000FeeExemptionTo:
      get(data, 'organization.organizationFees.purchaseForMoreThan15000FeeExemptionTo', '') ||
      '',
    mainLocation: {
      postalCode: {
        id: get(data, 'organization.mainLocation.postalCode.id', '') || '',
        postcode: get(data, 'organization.mainLocation.postalCode.postcode', '') || '',
        value: get(data, 'organization.mainLocation.postalCode.id', '') || '',
      },
      name: get(data, 'organization.mainLocation.name', '') || '',
      version: get(data, 'organization.mainLocation.version', '') || '',
    },
    address: get(data, 'organization.mainLocation.address', '') || '',
    address2: get(data, 'organization.mainLocation.address2', '') || '',
    city: get(data, 'organization.mainLocation.city', '') || '',
    county: get(data, 'organization.mainLocation.county', '') || '',
    additionalLocation: get(data, 'organization.additionalLocation', []) || [],
    locations: (get(data, 'organization.additionalLocation', []) || []).map((location) => ({
      id: get(location, 'id', '') || '',
      postalCode: {
        id: get(location, 'postalCode.id', '') || '',
        postcode: get(location, 'postalCode.postcode', '') || '',
        value: get(location, 'postalCode.id', '') || '',
      },
      name: get(location, 'name', '') || '',
      address: get(location, 'address', '') || '',
      address2: get(location, 'address2', '') || '',
      city: get(location, 'city', '') || '',
      county: get(location, 'county', '') || '',
      canBeDeleted: get(location, 'canBeDeleted', true),
    })) || [
      {
        id: '',
        postalCode: { id: '', postcode: '', value: '' },
        name: '',
        address: '',
        address2: '',
        city: '',
        county: '',
      },
    ],
    deletedOn: get(data, 'organization.deletedOn', null),
  };

  if (organization.organizationGroup) {
    organization.organizationGroup.label = organization.organizationGroup.name;
    organization.organizationGroup.value = organization.organizationGroup.id;
  }

  const values = {
    ...mainProfile,
    ...organization,
  };

  const isDisabled = values.deletedOn;

  const handleSubmit = (values, { setSubmitting }) => {
    // if activating a PENDING dealer, set trial exemption to today + 90 days
    try {
      if (values?.status === DealerStatus.PENDING && statusValue === DealerStatus.ACTIVE) {
        const addDays = (date, days) => {
          const d = new Date(date);
          d.setDate(d.getDate() + days);
          return d;
        };
        const today = new Date();
        const in90Days = addDays(today, 90);
        const formatYMD = (d) => d.toISOString().slice(0, 10);
        // ensure exemption flag is true and override date
        if (
          !values.monthlySubscriptionFeeExemption ||
          typeof values.monthlySubscriptionFeeExemption !== 'object'
        ) {
          values.monthlySubscriptionFeeExemption = { label: 'Yes', value: true };
        } else {
          values.monthlySubscriptionFeeExemption = {
            ...values.monthlySubscriptionFeeExemption,
            label: 'Yes',
            value: true,
          };
        }
        const promoCodeLength = values.promoCode?.trim()?.length;
        if (!promoCodeLength || promoCodeLength === 0) {
          values.monthlySubscriptionFeeExemptionTo = formatYMD(in90Days);
        }
      }
    } catch (e) {
      // silent fail for date preparation; continue submission
    }

    dispatch(editDealer(id, values, statusValue))
      .then(() => {
        setSubmitting(false);
      })
      .catch((error) => {
        setSubmitting(false);
        dispatch(showValidationAlert(error, AlertVariant.DANGER));
      });
  };

  const showMoreNotes = () => {
    if (dealerNotesRef.current) {
      setShowNotes({ show: true });
      dealerNotesRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const changeStatus = (value) => {
    // if activating from PENDING, prefill exemption fields visually
    if (value === DealerStatus.ACTIVE && values.status === DealerStatus.PENDING) {
      try {
        const addDays = (date, days) => {
          const d = new Date(date);
          d.setDate(d.getDate() + days);
          return d;
        };
        const formatYMD = (d) => d.toISOString().slice(0, 10);
        const today = new Date();
        const in90Days = addDays(today, 90);
        const setFV = formikSetFieldValueRef.current;
        if (typeof setFV === 'function') {
          setFV('monthlySubscriptionFeeExemption', { label: 'Yes', value: true }, false);
          const promoCodeLength = values.promoCode?.trim()?.length;
          if (!promoCodeLength || promoCodeLength === 0) {
            setFV('monthlySubscriptionFeeExemptionTo', formatYMD(in90Days), false);
          }
        }
      } catch (e) {
        // ignore visual prep errors
      }
    }

    if (statusValue === newStatusValue) {
      setNewStatusValue(false);
      setStatusValue(null);
    } else {
      setNewStatusValue(value);
      setStatusValue(value);
    }
  };

  const renderStatusButtons = () => {
    switch (values.status) {
      case DealerStatus.PENDING:
        return (
          <ButtonGroup className="d-flex gap-1">
            {newStatusValue && (
              <span className="dealer-status-button">Click SAVE to confirm this change.</span>
            )}
            <Button
              type="button"
              className={classNames(
                'rounded-pill',
                newStatusValue === DealerStatus.ACTIVE ? 'btn-primary' : 'btn-outline-primary',
              )}
              onClick={() => changeStatus(DealerStatus.ACTIVE)}
            >
              {DealerStatusActionName.ACTIVATE}
            </Button>
            <Button
              type="button"
              className={classNames(
                'rounded-pill',
                newStatusValue === DealerStatus.REJECTED
                  ? 'btn-primary'
                  : 'btn-outline-primary',
              )}
              onClick={() => changeStatus(DealerStatus.REJECTED)}
            >
              {DealerStatusActionName.REJECT}
            </Button>
          </ButtonGroup>
        );
      case DealerStatus.ACTIVE:
        return (
          <ButtonGroup>
            {newStatusValue && (
              <span className="dealer-status-button">Click SAVE to confirm this change.</span>
            )}
            <Button
              type="button"
              className={classNames(
                'rounded-pill',
                newStatusValue === DealerStatus.BLOCKED
                  ? 'btn-primary'
                  : 'btn-outline-primary',
              )}
              onClick={() => changeStatus(DealerStatus.BLOCKED)}
            >
              {DealerStatusActionName.BLOCK}
            </Button>
          </ButtonGroup>
        );
      case DealerStatus.BLOCKED:
        return (
          <ButtonGroup>
            {newStatusValue && (
              <span className="dealer-status-button">Click SAVE to confirm this change.</span>
            )}
            <Button
              type="button"
              className={classNames(
                'rounded-pill',
                newStatusValue === DealerStatus.ACTIVE ? 'btn-primary' : 'btn-outline-primary',
              )}
              onClick={() => changeStatus(DealerStatus.ACTIVE)}
            >
              {DealerStatusActionName.ACTIVATE}
            </Button>
          </ButtonGroup>
        );
      default:
        return null;
    }
  };

  const breadcrumbs = [
    { id: 1, type: 'home' },
    { id: 2, type: 'link', text: 'Dealers', url: '/dealers' },
    { id: 3, type: 'span', text: 'Edit Dealer' },
  ];

  return (
    <Container fluid>
      <PageBreadcrumb items={breadcrumbs} pageTitle="Edit Dealer" />
      <Row>
        <Col md="12">
          {isDisabled ? (
            <p className="dealer-form-delete-status">COMPANY DELETED</p>
          ) : (
            <Button
              className="float-end mb-3 rounded-pill btn-danger"
              onClick={() => setShowAlert(true)}
            >
              Delete
            </Button>
          )}
        </Col>
      </Row>
      <DealerSectionsNav id={id} />
      <ComponentCard
        title={
          <div className="d-flex align-items-center gap-3">
            <h5 className="card-title h5">Dealer Details</h5>
            <h5 className="card-title h5">
              Status: <span className="fw-bold">{DealerUserStatusName[values.status]}</span>
            </h5>
          </div>
        }
      >
        <div className="d-flex justify-content-end mb-2">{renderStatusButtons()}</div>
        <DealerNotesForm
          data={itemsNotes}
          showMoreNotes={showMoreNotes}
          disabled={isDisabled}
          searchParams={searchParamsNotes}
        />
        <Formik
          initialValues={values}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnBlur
          enableReinitialize
        >
          {({ values, errors, setFieldValue }) => {
            // expose Formik setFieldValue for status button handlers
            formikSetFieldValueRef.current = setFieldValue;
            return (
              <DealerForm
                values={values}
                errors={errors}
                setStatusValue={setStatusValue}
                statusValue={statusValue}
                setShowNotes={setShowNotes}
                showNotes={showNotes}
                searchParamsNotes={searchParamsNotes}
                changeOffsetNotes={changeOffsetNotes}
                dealerNotesRef={dealerNotesRef}
                isDisabled={isDisabled}
              />
            );
          }}
        </Formik>
      </ComponentCard>
      <SweetAlert
        danger
        showCancel={false}
        showConfirm={false}
        show={showAlert}
        title="Warning!"
        onConfirm={() => {
          setShowAlert(false);
          dispatch(deleteDealer(id));
        }}
        onCancel={() => setShowAlert(false)}
      >
        {(renderProps) => (
          <div>
            <p>Are you sure to remove this dealer?</p>
            <div className="d-flex justify-content-center gap-2 mt-3">
              <Button className="rounded-pill btn-soft-primary" onClick={renderProps.cancel}>
                Cancel
              </Button>
              <Button className="rounded-pill btn-primary" onClick={renderProps.confirm}>
                Yes, Delete
              </Button>
            </div>
          </div>
        )}
      </SweetAlert>
    </Container>
  );
}

export default EditDealerPage;
